import { useCallback, useMemo } from 'react';
import { Navigate, useNavigate, useRouterState } from '@tanstack/react-router';

import { useGetPageProducts } from '@/api/hooks/queries/use-get-product-catalog-query';
import { Button } from '@/components/ui/button.tsx';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card.tsx';
import { useProductSelectionStep } from '@/hooks/use-product-selection-step';
import { useSessionSave } from '@/hooks/use-session-save';
import {
  addProductIds,
  removeProductIds,
  useSessionStore,
} from '@/hooks/use-session-store/use-session-store';
import { LOGGER_ACTIONS, logger } from '@/lib/logger';
import { type ProductStep, getProductStepByUrl } from '@/lib/products';
import { getActionablePlanFamilyCarrierTypes } from '@/types/insurance-builder.type.ts';
import type { CategoryType } from '@/types/product.type.ts';
import ProductStepper from '@/views/layouts/ProductStepper.tsx';
import ProductCheckboxGroup from '@/views/products/components/ProductCheckboxGroup.tsx';
import ProductRadioGroup from '@/views/products/components/ProductRadioGroup.tsx';
import { ProductSelectionSubmitFeedback } from '@/views/products/components/ProductSelectionSubmitFeedback.tsx';
import SkipButton from '@/views/products/components/SkipButton.tsx';

// Every product route renders this page; the category comes from the config entry whose url matches
export default function SelectProducts() {
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const step = getProductStepByUrl(pathname);

  // Every route rendering this page has a step, so a miss means the pathname already moved on to
  // a non-product route while this page is still mounted. Redirecting would hijack that navigation.
  if (!step?.categoryType) return null;

  return <ProductSelection key={step.id} step={step} categoryType={step.categoryType} />;
}

type ProductSelectionProps = {
  step: ProductStep;
  categoryType: CategoryType;
};

function ProductSelection({ step, categoryType }: ProductSelectionProps) {
  const navigate = useNavigate();
  const { saveSession } = useSessionSave();
  const { insuranceBuilder, selectedProductIds } = useSessionStore();
  const { nextStep } = useProductSelectionStep();

  const { products, isSuccess } = useGetPageProducts({
    categoryType,
    patientCarrierTypes: getActionablePlanFamilyCarrierTypes(insuranceBuilder),
  });

  const selectedId = useMemo(
    () => products.find((product) => selectedProductIds.includes(product.uuid))?.uuid ?? '',
    [products, selectedProductIds]
  );

  const numProducts = useMemo(
    () => products.filter((product) => selectedProductIds.includes(product.uuid)).length,
    [products, selectedProductIds]
  );

  const selectOne = useCallback(
    (productId: string) => {
      // An empty id is a deselection
      const kept = productId ? [productId] : [];
      removeProductIds(products.filter((p) => !kept.includes(p.uuid)).map((p) => p.uuid));
      addProductIds(kept);
    },
    [products]
  );

  const selectMany = useCallback(
    (productIds: string[]) => {
      removeProductIds(products.filter((p) => !productIds.includes(p.uuid)).map((p) => p.uuid));
      addProductIds(productIds);
    },
    [products]
  );

  const skipSelection = () => {
    removeProductIds(products.map((product) => product.uuid));
    // Auto-save session (non-blocking)
    saveSession();

    logger.action(LOGGER_ACTIONS.INTAKE.PRODUCT_SELECTION_SKIPPED, {
      product_category: categoryType,
    });
    navigate({ to: nextStep });
  };

  const handleAddToOrder = () => {
    // Auto-save session (non-blocking)
    saveSession();
    navigate({ to: nextStep });
  };

  // Direct URL visits, refreshes and wizard resume all bypass step derivation
  const noEligibleProducts = isSuccess && products.length === 0;
  if (noEligibleProducts) {
    return <Navigate to={nextStep} replace />;
  }

  return (
    <section>
      <ProductStepper />
      <div className="space-y-6">
        <Card spacing="sm">
          <CardHeader>
            <CardTitle>{step.pageTitle}</CardTitle>
            {step.pageDescription ? (
              <CardDescription>{step.pageDescription}</CardDescription>
            ) : null}
          </CardHeader>
          <CardContent className="space-y-6">
            {step.selectionMode === 'single' ? (
              <ProductRadioGroup products={products} value={selectedId} onValueChange={selectOne} />
            ) : (
              <ProductCheckboxGroup
                allProducts={products}
                selectedProductIds={selectedProductIds}
                onSelectedChange={selectMany}
              />
            )}
          </CardContent>
        </Card>

        <div className="grid grid-cols-[1fr_3fr] gap-3">
          <SkipButton categoryType={categoryType} onSkip={skipSelection} />
          <Button onClick={handleAddToOrder}>Add to order ({numProducts})</Button>
        </div>
        <ProductSelectionSubmitFeedback currentCategory={categoryType} />
      </div>
    </section>
  );
}
